<template>
    <div>
        <div id="toplist-loading" v-if="initialLoading"><div class="toplist__spinner"></div></div>
        <div class="toplist__content" v-else :class="{ 'toplist__content--loading': loading }">
            <div class="toplist__loading-overlay" v-if="loading"><div class="toplist__spinner"></div></div>

            <form v-if="companyYears.length > 1" class="select select--competitions bedrift-competition-select" @submit.prevent>
                <select v-model="competitionId">
                    <option v-for="year in companyYears" :key="year.competition_id" :value="year.competition_id">{{ year.name }}</option>
                </select>
            </form>

            <div class="table__summary-wrapper">
                <div class="table__summary">
                    <div class="table__summary-item hero__stat">
                        <div class="hero__statcount">{{ stats.employees }}</div>
                        <div class="hero__stat-text">Ansatte</div>
                    </div>
                    <div class="table__summary-item hero__stat">
                        <div class="hero__statcount">{{ stats.active_employees }}</div>
                        <div class="hero__stat-text">Aktive Ansatte</div>
                    </div>
                    <div class="table__summary-item hero__stat">
                        <div class="hero__statcount">{{ stats.registrations }}</div>
                        <div class="hero__stat-text">Registreringer</div>
                    </div>
                    <div class="table__summary-item hero__stat">
                        <div class="hero__statcount">{{ stats.destinations }}</div>
                        <div class="hero__stat-text">Unike Turmål</div>
                    </div>
                </div>
                <div class="table__summary" v-if="stats.distance || stats.elevation">
                    <div class="table__summary-item hero__stat" v-if="stats.distance">
                        <div class="hero__statcount">{{ stats.distance }} km</div>
                        <div class="hero__stat-text">Distanse</div>
                    </div>
                    <div class="table__summary-item hero__stat" v-if="stats.elevation">
                        <div class="hero__statcount">{{ stats.elevation }}</div>
                        <div class="hero__stat-text">Høydemeter</div>
                    </div>
                </div>
            </div>

            <h3>Ansatte hos {{ companyName }}</h3>
            <p v-if="!topList.length">Ingen registreringer ennå.</p>
            <div class="table" v-else>
                <table>
                    <thead>
                        <tr>
                            <th class="table__header--ranking">#</th>
                            <th class="table__header--name">Navn</th>
                            <th style="text-align: right;">
                                <a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'registrations' }" @click.prevent="order = 'registrations'">
                                    Registreringer<i v-if="order === 'registrations'" class="pi pi-sort-amount-down"></i>
                                </a>
                            </th>
                            <th style="text-align: right;">
                                <a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'destinations' }" @click.prevent="order = 'destinations'">
                                    Turmål<i v-if="order === 'destinations'" class="pi pi-sort-amount-down"></i>
                                </a>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="entry in topList" :key="entry.person_id" class="table__entry">
                            <td class="table__ranking table__num">{{ entry.ranking }}</td>
                            <td class="table__name">
                                <a v-if="isAdmin" :href="`/bedrift/kollega/${entry.person_id}`" class="table__secondary-link">{{ entry.name }}</a>
                                <template v-else>{{ entry.name }}</template>
                            </td>
                            <td class="table__reg table__num">{{ entry.registrations }}</td>
                            <td class="table__reg table__num">{{ entry.destinations }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="bedrift-companies-link">
                <a href="/topplister?type=companies" class="action">
                    <i class="pi pi-building"></i>
                    Se toppliste bedrifter
                </a>
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';

const props = defineProps({
    companyName: { type: String, required: true },
    competitionId: { type: Number, default: null },
});

const order = ref('destinations');
// A separate, mutable ref from props.competitionId (only used as the
// initial value) - the select below drives its own AJAX-fetched state from
// here on, same as order.
const competitionId = ref(props.competitionId);
const companyYears = ref([]);
const initialLoading = ref(true);
const loading = ref(true);

const stats = ref({ employees: 0, active_employees: 0, registrations: 0, destinations: 0, distance: 0, elevation: 0 });
const topList = ref([]);
const isAdmin = ref(false);

let requestToken = 0;

async function load() {
    const token = ++requestToken;
    loading.value = true;

    const search = new URLSearchParams();
    search.set('order', order.value);
    if (competitionId.value) {
        search.set('competition_id', competitionId.value);
    }

    try {
        const response = await fetch('/xhr/frontend/companies/toplist?' + search.toString(), {
            headers: { Accept: 'application/json' },
        });
        if (response.ok && token === requestToken) {
            const data = await response.json();
            // The server can resolve a different competition than requested
            // (e.g. one the company was never active for falls back to
            // current) - sync back so the select reflects what's actually
            // showing, same as the old server-rendered `selected` did.
            competitionId.value = data.competitionId;
            companyYears.value = data.companyYears;
            stats.value = data.stats;
            topList.value = data.topList;
            isAdmin.value = data.isAdmin;
        }
    } catch (e) {
        // Leave the previous state in place.
    } finally {
        if (token === requestToken) {
            loading.value = false;
            initialLoading.value = false;
        }
    }
}

watch([order, competitionId], load);
onMounted(load);
</script>

<style scoped>
/* Two manually-grouped rows (Ansatte/Aktive ansatte/Registreringer/Turmål,
then Distanse/Høydemeter below) - spread across the row instead of the
shared .table__summary's centered flow used elsewhere. */
.table__summary {
    justify-content: space-around;
}
</style>
